import { Card, CardContent, Typography, Skeleton } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { RevenueTrendDataPoint } from '../../../../types/admin';

interface RevenueTrendChartProps {
  data: RevenueTrendDataPoint[];
  loading?: boolean;
}

export default function RevenueTrendChart({ data, loading }: RevenueTrendChartProps) {
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Revenue Trend
          </Typography>
          <Skeleton variant="rectangular" height={300} />
        </CardContent>
      </Card>
    );
  }

  const historicalData = data.filter((d) => !d.is_forecast);
  const forecastData = data.filter((d) => d.is_forecast);

  // Include the last historical point in forecast data for smooth connection
  const lastHistoricalPoint = historicalData[historicalData.length - 1];
  const fullForecastData = lastHistoricalPoint ? [lastHistoricalPoint, ...forecastData] : forecastData;

  const xAxisData = data.map((d) => d.date);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Revenue Trend
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          12-month history with 3-month forecast
        </Typography>
        <LineChart
          xAxis={[
            {
              data: xAxisData,
              scaleType: 'point',
              label: 'Month',
            },
          ]}
          series={[
            {
              data: historicalData.map((d) => d.mrr),
              label: 'MRR (Actual)',
              color: '#1976d2',
              curve: 'monotoneX',
            },
            {
              data: data.map((d) => {
                // Only show forecast data points
                const forecastPoint = fullForecastData.find(f => f.date === d.date);
                return forecastPoint ? forecastPoint.mrr : null;
              }),
              label: 'MRR (Forecast)',
              color: '#90caf9',
              curve: 'monotoneX',
              connectNulls: true,
            },
          ]}
          height={300}
          slotProps={{
            legend: {
              direction: 'row',
              position: { vertical: 'top', horizontal: 'middle' },
              padding: 0,
            },
          }}
        />
      </CardContent>
    </Card>
  );
}
